import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Dimmer, Label, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { maskCpfOrCnpj } from '../../../../../../services/masks';
import { translatePropertyStatus } from '../../../../util';
import {
  getClaimGaranttiLogs,
  updateClaimGaranttiLogsViewed
} from '../../../../../../services/claim';
import { warningNotification } from '../../../../../../services/notification';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';
const GARANTTI_ORANGE = '#f2711c';

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI']);

export const sanitizeGaranttiNoteHtml = (html?: string | null): string => {
  if (!html || typeof window === 'undefined') {
    return '';
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Element) => {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const element = child as Element;
      const tag = element.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'IFRAME' || tag === 'OBJECT') {
        element.remove();
        return;
      }
      walk(element);
      Array.from(element.attributes).forEach(attr => element.removeAttribute(attr.name));
      if (!ALLOWED_TAGS.has(tag)) {
        const parent = element.parentNode;
        if (parent) {
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        }
      }
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
};

export const parseGaranttiUserName = (
  userName?: string | null
): { name: string; affiliation: string } => {
  const raw = String(userName || '').trim();
  const separator = raw.indexOf(' - ');
  if (separator === -1) {
    return { name: raw || 'Garantti', affiliation: '' };
  }
  return {
    name: raw.slice(0, separator).trim(),
    affiliation: raw.slice(separator + 3).trim()
  };
};

const isGaranttiSide = (name: string, affiliation: string) =>
  `${name} ${affiliation}`.toLowerCase().includes('garantti');

const isBrokerSide = (name: string, affiliation: string) =>
  /confiax|lado bom|4seg|redevistorias|rede vistorias|\brv\b/.test(
    `${name} ${affiliation}`.toLowerCase()
  );

const InfoContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  border-left: 4px solid ${CLAIM_UPDATES_ACCENT_COLOR};
  padding: 0 0 0 12px;
  border-radius: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 0.7fr) minmax(0, 0.8fr) minmax(0, 1.4fr);
  gap: 16px 24px;
  align-items: flex-start;
  color: #2f3441;
  margin: 0 0 22px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;

  > strong {
    color: #707682;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.15;
  }

  > span {
    color: #2f3441;
    font-size: 14px;
    line-height: 1.35;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const InfoSecondary = styled.span`
  color: #707682 !important;
  font-size: 12px !important;
  line-height: 1.25 !important;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionLabel = styled.div`
  color: #2f3441;
  font-size: 14px;
  font-weight: 700;
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NoteCard = styled.article<{ $unread: boolean; $side: 'garantti' | 'broker' | 'other' }>`
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${props => {
    if (props.$unread) return GARANTTI_ORANGE;
    if (props.$side === 'garantti') return '#fbbd88';
    if (props.$side === 'broker') return CLAIM_UPDATES_ACCENT_COLOR;
    return '#d1d5db';
  }};
  border-radius: 8px;
  padding: 14px 16px;
  background: #ffffff;
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const AuthorBlock = styled.div`
  min-width: 0;
`;

const AuthorName = styled.div`
  color: #2f3441;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
`;

const NoteDate = styled.span`
  color: #707682;
  font-size: 12px;
  white-space: nowrap;
`;

const NoteBody = styled.div<{ $expanded: boolean }>`
  color: #2f3441;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
  max-height: ${props => (props.$expanded ? 'none' : 'calc(1.45em * 8)')};
  overflow: hidden;

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const ExpandButton = styled.button`
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${CLAIM_UPDATES_ACCENT_COLOR};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const EmptyState = styled.div`
  color: #707682;
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
`;

type GaranttiLog = {
  id: string;
  externalId?: string;
  eventDate: string;
  eventTime: string;
  title: string;
  description?: string | null;
  viewed: boolean;
  wasUnread: boolean;
};

type GaranttiLogsResponse = {
  data: GaranttiLog[];
  linked: boolean;
  garanttiSinisterId?: string | null;
  certificateNumber?: string | null;
};

type GaranttiLogsTabProps = {
  id: string;
  claim: any;
};

const formatGaranttiDateTime = (eventDate?: string, eventTime?: string) => {
  const parsed = moment.tz(
    `${eventDate || ''} ${eventTime || ''}`.trim(),
    ['YYYYMMDD HH:mm:ss', 'YYYYMMDD H:mm:ss'],
    true,
    'America/Sao_Paulo'
  );
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }
  return `${eventDate || ''} ${eventTime || ''}`.trim() || '-';
};

const NoteItem = ({ note }: { note: GaranttiLog }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const { name, affiliation } = parseGaranttiUserName(note.title);
  const side = isGaranttiSide(name, affiliation)
    ? 'garantti'
    : isBrokerSide(name, affiliation)
      ? 'broker'
      : 'other';
  const html = useMemo(() => sanitizeGaranttiNoteHtml(note.description), [note.description]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) {
      return;
    }
    setCanExpand(el.scrollHeight > el.clientHeight + 1);
  }, [html, expanded]);

  return (
    <NoteCard $unread={note.wasUnread} $side={side}>
      <NoteHeader>
        <AuthorBlock>
          <AuthorName>{name}</AuthorName>
          {affiliation ? (
            <Label
              size="tiny"
              color={side === 'garantti' ? 'orange' : undefined}
              basic={side === 'other'}
              style={
                side === 'broker'
                  ? { backgroundColor: '#59249B', color: '#ffffff', border: 0, marginTop: 6 }
                  : { marginTop: 6 }
              }
            >
              {affiliation.replace(/ - /g, ' · ')}
            </Label>
          ) : null}
        </AuthorBlock>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {note.wasUnread ? (
            <Label size="tiny" color="orange">
              Novo
            </Label>
          ) : null}
          <NoteDate>{formatGaranttiDateTime(note.eventDate, note.eventTime)}</NoteDate>
        </div>
      </NoteHeader>
      {html ? (
        <>
          <NoteBody
            ref={bodyRef}
            $expanded={expanded}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          {canExpand ? (
            <ExpandButton type="button" onClick={() => setExpanded(value => !value)}>
              {expanded ? 'Ver menos' : 'Ver mais'}
            </ExpandButton>
          ) : null}
        </>
      ) : null}
    </NoteCard>
  );
};

const GaranttiLogsTab = ({ id, claim }: GaranttiLogsTabProps) => {
  const {
    propertyStreet,
    propertyNumber,
    propertyComplement,
    propertyDistrict,
    propertyCity,
    propertyState,
    tenantName,
    tenantDocument,
    estateName,
    createdAt,
    propertyStatus
  } = claim || {};
  const [logs, setLogs] = useState<GaranttiLog[]>([]);
  const [meta, setMeta] = useState<Omit<GaranttiLogsResponse, 'data'>>({
    linked: false,
    garanttiSinisterId: null,
    certificateNumber: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const fullAddress = `${propertyStreet || ''}, ${propertyNumber || ''}${
    propertyComplement ? ` ${propertyComplement}` : ''
  } - ${propertyDistrict || ''} - ${propertyCity || ''} - ${propertyState || ''}`;
  const allUnread = logs.length > 0 && logs.every(item => !item.viewed);

  useEffect(() => {
    let cancelled = false;

    const loadLogs = async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const response = await getClaimGaranttiLogs(id);
        if (cancelled) {
          return;
        }
        const list = (response.data || []).map(item => ({
          ...item,
          wasUnread: item.viewed === false
        }));
        setMeta({
          linked: Boolean(response.linked),
          garanttiSinisterId: response.garanttiSinisterId,
          certificateNumber: response.certificateNumber
        });
        const unreadCount = list.filter(item => item.wasUnread).length;
        if (unreadCount > 0) {
          await updateClaimGaranttiLogsViewed(id, true);
          if (cancelled) {
            return;
          }
          setLogs(list.map(item => ({ ...item, viewed: true })));
        } else {
          setLogs(list);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(true);
          setLogs([]);
          warningNotification(
            'Ops...',
            'Não foi possível carregar o histórico da Garantti. Tente de novo.'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadLogs();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleMarkUnread = async () => {
    if (!logs.length || isUpdating || allUnread) {
      return;
    }
    setIsUpdating(true);
    try {
      await updateClaimGaranttiLogsViewed(id, false);
      setLogs(prev => prev.map(item => ({ ...item, viewed: false, wasUnread: true })));
    } catch (error) {
      warningNotification(
        'Ops...',
        'Não foi possível marcar o histórico como não lido.'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const emptyMessage = loadError
    ? 'Não foi possível carregar o histórico da Garantti. Tente de novo.'
    : !meta.linked
      ? 'Ainda não encontramos este sinistro no portal da Garantti. Confira o código do sinistro ou o protocolo.'
      : 'Nenhuma atualização importada da Garantti ainda.';

  return (
    <>
      {isLoading ? (
        <Dimmer active inverted>
          <Loader content="Carregando conversa da Garantti..." />
        </Dimmer>
      ) : (
        <>
          <InfoContainer>
            <InfoItem>
              <strong>Inquilino</strong>
              <span>{tenantName}</span>
              {tenantDocument && (
                <InfoSecondary>{maskCpfOrCnpj(tenantDocument)}</InfoSecondary>
              )}
            </InfoItem>
            <InfoItem>
              <strong>Imobiliária</strong>
              <span>{estateName}</span>
            </InfoItem>
            <InfoItem>
              <strong>Data de abertura</strong>
              <span>{createdAt ? moment(createdAt).format('DD/MM/YYYY') : '-'}</span>
            </InfoItem>
            <InfoItem>
              <strong>Protocolo Garantti</strong>
              <span>{meta.certificateNumber || '-'}</span>
            </InfoItem>
            <InfoItem>
              <strong>Imóvel</strong>
              <span>{fullAddress}</span>
              {propertyStatus && (
                <InfoSecondary>{translatePropertyStatus(propertyStatus)}</InfoSecondary>
              )}
            </InfoItem>
          </InfoContainer>

          <ActionBar>
            <SectionLabel>Conversa Garantti</SectionLabel>
            <Button
              basic
              type="button"
              disabled={!logs.length || isUpdating || allUnread}
              onClick={handleMarkUnread}
              content="Marcar como não lidas"
            />
          </ActionBar>

          {logs.length === 0 ? (
            <EmptyState>{emptyMessage}</EmptyState>
          ) : (
            <Thread>
              {logs.map(note => (
                <NoteItem key={note.id || note.externalId} note={note} />
              ))}
            </Thread>
          )}
        </>
      )}
    </>
  );
};

export default GaranttiLogsTab;
